import React, { useState, useRef, useEffect } from 'react';
import CustomButton from '../../components/CustomButton';
import Color from '../../../utils/Color';
import CustomText, {
  getFontSize,
  getFontWeight
} from '../../components/CustomText';
import './OTPInput.css';
import { makeStyles, useTheme, withStyles } from "@material-ui/core/styles";
import { useHistory } from 'react-router-dom';
import CustomBoardNewLogin from '../../components/CustomBoardNewLogin';
import * as selector from '../../../common/selectors';
import * as registrationAction from '../../../common/actions/registration';
import AuthManager from '../../../utils/AuthManager';
import { useSelector, useDispatch } from 'react-redux';
import CustomToast from '../../components/CustomToast';
import LoadingOverlay from '../../components/LoadingOverlay';
import { Grid } from '@material-ui/core';
import CustomModal from '../../components/CustomModal';

// -------------------------------------
// 1) useStyles with breakpoints for responsiveness
// -------------------------------------
const useStyles = makeStyles((theme) => ({
  container: {
    // Default (desktop) padding
    paddingTop: "3.5rem",
    paddingLeft: '2.2rem',
    paddingRight: '2.2rem',
    [theme.breakpoints.down('sm')]: {
      // Tighter padding on mobile
      paddingTop: "0rem",
      paddingLeft: '0rem',
      paddingRight: '0rem',
    },
  },
  headerTextStyle: {
    fontWeight: 700,
    fontSize: 20,
    color: "#333333",
    [theme.breakpoints.down('sm')]: {
      // Slightly smaller on mobile
      fontSize: 18,
    },
  },
  subHeaderText: {
    fontWeight: 500,
    fontSize: 14,
    color: '#333333',
    marginTop: '0.6rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
    },
  },
  otpContainer: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
    marginTop: '2rem',
    [theme.breakpoints.down('xs')]: {
      // Optionally wrap if screen is very small
      // flexWrap: 'wrap',
      gap: '8px',
    },
  },
  otpInput: {
    textAlign: 'center',
    fontSize: '16px',
    width: '48px',
    height: '58px',
    border: '1px solid #C6C7C9',
    [theme.breakpoints.down('xs')]: {
      // Slightly shrink on very small screens
      width: '37px',
      height: '50px',
    },
  },
  resendText: {
    color: '#0D99FF',
    textDecoration: 'underline',
    fontSize: '12px',
    cursor: 'pointer',
    marginTop: '1rem',
    fontWeight: 500,
  },
  buttonStyle: {
    height: '48px',
    [theme.breakpoints.down('sm')]: {
      height: '45px',
    },
  },
  helpText: {
    marginTop: '3.5rem',
    fontSize: '12px',
    color: '#818181',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      marginTop: '2rem',
      fontSize: '11px',
    },
  },
  modalDesign: {
    padding: 0,
    textAlign: 'center',
    paddingLeft: 15,
    paddingRight: 15,
    paddingBottom: 5,
    [theme.breakpoints.down("xs")]: {
      minWidth: 200,
      marginTop: 20,
      marginBottom: 20,
    },
    [theme.breakpoints.up("sm")]: {
      minWidth: 250,
    },
    [theme.breakpoints.up("lg")]: {
      minWidth: 250,
      maxWidth: 250,
    },
    textInputStyle: {
      height: 35,
      marginLeft: 5,
      marginRight: 5,
    },
    containerStyle: {
      flexGrow: 1,
      marginBottom: 30,
      paddingRight: 20,
      marginBottom: 30
    },
    tooltipContainer: {
      display: "flex",
      alignItems: "center",
    },
    titleTextStyle: {
      fontWeight: 'bold',
      fontSize: 25,
    },
  },
}));

// -------------------------------------
// Main Component
// -------------------------------------
const MobileOtp = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  // 2) OTP input states
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [currentFocus, setCurrentFocus] = useState(0);
  const inputRefs = useRef([]);

  // 3) Extra states for logic
  const [resendOtp, setResendOtp] = useState(false);
  const [verifyOTPAction, setVerifyOTPAction] = useState(false);
  const [customToastStatus, setCustomToastStatus] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [localNric, setLocalNric] = useState('');
  const [localOtpRef, setLocalOtpRef] = useState('');
  const [localOtp, setLocalOtp] = useState('');
  const [localClientMobile, setLocalClientMobile] = useState('');
  const [localClientMobileLast4Digits, setLocalClientMobileLast4Digits] = useState('');
  const [localCountryCode, setLocalCountryCode] = useState('');
  const [asterisks, setAsterisks] = useState('');
  const [isVisible, setIsVisible] = useState('');

  // 4) Redux selectors
  const registrationOTPVerifyData = useSelector(selector.registrationOTPVerifyData);
  const registrationOTPVerifyLoading = useSelector(selector.registrationOTPVerifyLoading);
  const registrationOtpResendData = useSelector(selector.registrationOtpResendData);
  const registrationOtpResendLoading = useSelector(selector.registrationOtpResendLoading);
  const promptMobilePhoneInputData = useSelector(selector.promptMobilePhoneInputData);
  const promptMobilePhoneInputLoading = useSelector(selector.promptMobilePhoneInputLoading);

  const getRegistrationCustomerDetailsData = useSelector(selector.getRegistrationCustomerDetailsData);
  const getRegistrationCustomerDetailsLoading = useSelector(selector.getRegistrationCustomerDetailsLoading);
  const getRegistrationCustomerDetailsStatus = useSelector(selector.getRegistrationCustomerDetailsStatus);

  // -------------------------------------
  // Focus correct input whenever currentFocus changes
  // -------------------------------------
  useEffect(() => {
    if (inputRefs.current[currentFocus]) {
      inputRefs.current[currentFocus].focus();
    }
  }, [currentFocus]);

  // -------------------------------------
  // Retrieve local storage data
  // -------------------------------------
  useEffect(() => {
    const init = async () => {
      const nric = await AuthManager.retrieveNric();
      if (nric) setLocalNric(nric);

      const otpRef = await AuthManager.retrieveOtpReference();
      if (otpRef) setLocalOtpRef(otpRef);

      const mobile = await AuthManager.retrieveClientMobileNo();
      if (mobile) {
        setLocalClientMobile(mobile);
        const last4 = mobile.slice(-4);
        setLocalClientMobileLast4Digits(last4);

        const asteriskCount = Math.max(mobile.length - 4, 0); // ensure non-negative
        setAsterisks('*'.repeat(asteriskCount));  // create a string of asterisks
      }

      const countryCode = await AuthManager.retrieveMobileCountryCode();
      if (countryCode) setLocalCountryCode(countryCode);
    };
    init();
  }, []);

  // -------------------------------------
  // Handle getRegistrationCustomerDetails response
  // -------------------------------------
  useEffect(() => {
    if (verifyOTPAction && !getRegistrationCustomerDetailsLoading) {
      if (getRegistrationCustomerDetailsStatus && getRegistrationCustomerDetailsData) {
        const regData = getRegistrationCustomerDetailsData.Data;
        const regSuccess = getRegistrationCustomerDetailsData?.Success;
        if (regSuccess && (regData?.IsExceedAttempts !== true)) {
          localStorage.setItem("newRegCusDetail", JSON.stringify(regData));
          AuthManager.setOtpReference(localOtpRef);
          AuthManager.setOtpCode(localOtp)
          history.push("/OnboardingLanding");
        } else {
          if (regSuccess && (regData?.IsExceedAttempts === true)) {
            setIsVisible(true)
           // setCustomToastStatus(true);
            //setToastMessage(regData?.Message || regData?.ErrorMessage || 'You have entered an invalid OTP. Please try again.');
          } else {
            if (getRegistrationCustomerDetailsData?.StatusCode === 400) {
              //setIsVisible(true)
              setCustomToastStatus(true);
              setToastMessage(regData?.Message || regData?.ErrorMessage || 'You have entered an invalid OTP. Please try again.');
            } else {
              setCustomToastStatus(true);
              setToastMessage(regData?.Message || regData?.ErrorMessage ||'You have entered an invalid OTP. Please try again.');
            }
          }

        }
      } else if (!getRegistrationCustomerDetailsStatus && getRegistrationCustomerDetailsData === null) {
        setCustomToastStatus(true);
        setToastMessage("Your OTP code doesn't match. Please try again or request another OTP.");
      }
      setVerifyOTPAction(false);
    }
  }, [getRegistrationCustomerDetailsData, getRegistrationCustomerDetailsLoading, getRegistrationCustomerDetailsStatus, verifyOTPAction, history]);

  // -------------------------------------
  // OTP Input Handlers
  // -------------------------------------
  const handleInputChange = (index, value) => {
    let digit = value.replace(/\D/g, '').slice(-1);
    const newOtpValues = [...otpValues];
    newOtpValues[index] = digit;
    setOtpValues(newOtpValues);

    // Move to next input if a digit was entered and not on the last field
    if (digit && index < otpValues.length - 1) {
      setCurrentFocus(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    // Check for backspace
    if (event.key === 'Backspace') {
      if (!otpValues[index] && index > 0) {
        setCurrentFocus(index - 1);
      } else {
        const newOtpValues = [...otpValues];
        newOtpValues[index] = '';
        setOtpValues(newOtpValues);
      }
    }
  };

  // -------------------------------------
  // Resend OTP
  // -------------------------------------
  const onClickResend = async () => {
    // setResendOtp(true);
    // if (localClientMobile && localCountryCode) {
    //   dispatch(registrationAction.promptMobilePhoneInput(localNric, localClientMobile, localCountryCode,recaptchaToken));
    // } else {
    //   dispatch(registrationAction.registrationOtpResend(localNric));
    // }

    try {
      // Call reCAPTCHA API to get the token
      const recaptchaToken = await window.grecaptcha.execute(
        "6Lce7OMZAAAAALxOm9WwIETw1EL9UB73SDe4g0Tx",
        { action: "WEB_REGISTRATION_OTP" }
      );

      if (!recaptchaToken) {
        setCustomToastStatus(true)
        setToastMessage("Unable to verify reCAPTCHA. Please try again.")
        return;
      }

      // Update resendOtp state
      setResendOtp(true);

      // Decide which function to call based on the availability of localClientMobile and localCountryCode
      if (localClientMobile && localCountryCode !== "") {
        dispatch(registrationAction.promptMobilePhoneInput(localNric, localClientMobile, localCountryCode, recaptchaToken));
        //promptMobilePhoneInput(localNric, localClientMobile, localCountryCode, recaptchaToken);
      } else {
        dispatch(registrationAction.registrationOtpResend(localNric, recaptchaToken));
        //registrationOtpResend(localNric, recaptchaToken);
      }
    } catch (error) {
      setCustomToastStatus(true)
      setToastMessage("Error during reCAPTCHA verification. Please try again.")
    }
  };

  // -------------------------------------
  // Verify OTP
  // -------------------------------------
  const onClickVerify = () => {
    const code = otpValues.join('');
    if (!code) {
      setCustomToastStatus(true);
      setToastMessage('Please enter your OTP.');
      return;
    }

    setVerifyOTPAction(true);

    const mobilePhoneInputOtpRef = promptMobilePhoneInputData?.Data;
    const resendOtpReference = registrationOtpResendData?.items?.otpReference;

    // Decide which reference to use
    let finalOtpRef = localOtpRef;
    if (resendOtp && localCountryCode) {
      finalOtpRef = mobilePhoneInputOtpRef || resendOtpReference;
    } else if (resendOtp && !localCountryCode) {
      finalOtpRef = mobilePhoneInputOtpRef || resendOtpReference;
    }
    setLocalOtp(code);
    setLocalOtpRef(finalOtpRef);
    // Construct request data
    const inappValue = localStorage.getItem('inapp');
    const data = {
      "AuthCode": "",
      "AuthState": "",
      "Otp": code,
      "OtpReference": finalOtpRef,
      "InApp": inappValue === "1" ? "1" : ""
    };

    // Dispatch getRegistrationCustomerDetails
    dispatch(registrationAction.getRegistrationCustomerDetails(data));
  };

  // -------------------------------------
  // Toast Handler
  // -------------------------------------
  const handleCloseCustomToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setCustomToastStatus(false);
  };

  // -------------------------------------
  // Rendering the Page
  // -------------------------------------
  const renderPage = () => {
    return (
      <div className={classes.container}>
        <div className={classes.headerTextStyle}>Verify via Mobile Number</div>
        <div className={classes.subHeaderText}>
          {`OTP has been sent to ${asterisks}${localClientMobileLast4Digits}`}
        </div>

        <div className={classes.otpContainer}>
          {otpValues.map((val, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              maxLength={1}
              value={val}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              inputMode="numeric"
              className={classes.otpInput}
            />
          ))}
        </div>

        {/* Resend */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span className={classes.resendText} onClick={onClickResend}>
            Resend Password
          </span>
        </div>

        <div style={{ marginTop: '2.5rem' }}>
          <CustomButton
            title="Proceed"
            onClick={onClickVerify}
            design={classes.buttonStyle}
          />
        </div>

        <div className={classes.helpText}>
          If you encounter any issues, please contact our Customer Service Centre
          via our hotline +65 6887 8777 or WhatsApp us at +65 6887 8777
        </div>
      </div>
    );
  };

  const renderMaintenanceChild = () => <></>;


  const modalContainer = () => {
    return (
      <div className={classes.containerStyle}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className={classes.tooltipContainer}>
              <CustomText design={classes.titleTextStyle} style={{ marginBottom: 10 }}>
                {getRegistrationCustomerDetailsData?.Data?.Message || getRegistrationCustomerDetailsData?.Data?.ErrorMessage  || "You have entered an invalid OTP. Please try again."}
              </CustomText>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  };

  const onClickNextButton = () => {
    history.push("/")

  };


  // Dimensions for CustomBoardNewLogin
  const height = 800;
  const width = 600;

  return (
    <>
      <CustomBoardNewLogin
        maintenanceChild={renderMaintenanceChild()}
        children={renderPage()}
        height={height}
        width={width}
      />

      <CustomToast
        open={customToastStatus}
        handleCloseCustomToast={handleCloseCustomToast}
        title={toastMessage}
        warning
      />

      <CustomModal
        open={isVisible}
        //handleClose={handleClose}
        //titlestyle={classes.modalTitleStyle}
        modalDesign={classes.modalDesign}
        submitButtonText={"OK"}
        //cancelButtonText={"Cancel"}
        modalComponent={modalContainer()}
        disableBackdropClick
        handleSubmitButton={() => onClickNextButton()}
        isVisibleCloseButton={false}
        okBtnSty={true}
      // handleCancelButton={handleClose}
      />

      {(registrationOTPVerifyLoading
        || registrationOtpResendLoading
        || promptMobilePhoneInputLoading
        || getRegistrationCustomerDetailsLoading) && (
          <LoadingOverlay loading={true} />
        )}
    </>
  );
};

export default MobileOtp;
